import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { brand } from "../config";
import mantisLogoLight from "../Assets/img/mantisBlackLogo.png";
import {
  CheckCircle2,
  Loader2,
  Circle,
  XCircle,
  FileText,
  ClipboardCheck,
  Users,
  FileSearch,
  GitPullRequestArrow,
  Truck,
  AlertTriangle,
  LockKeyhole,
  Info,
  ThumbsUp,
  PackageCheck,
  Briefcase,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Brain,
  TrendingUp,
  DollarSign,
  Shield,
  Package,
  ArrowDown,
} from "lucide-react";
import {
  getWorkflowResultsByIdP2O,
  getOrderItems,
} from "../ApiCalls/mantisFlowsManangment";
import { getLogsByOrderId } from "../ApiCalls/mantisFlowsLogs";
import "./RenderOrder.scss";

// Utility functions for comments section
const getInitials = (name) => {
  if (!name) return "U";
  const names = name.split(" ");
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return "bg-blue-500";

  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-teal-500",
    "bg-orange-500",
    "bg-cyan-500",
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const displayStepsConfig = [
  {
    id: 1,
    name: "Solicitud Creada",
    internalKey: "request_created",
    icon: <FileText />,
    summary: "El IPR ha sido registrado exitosamente en el sistema.",
    apiStepMapping: null,
  },
  {
    id: 2,
    name: "Revisión de Solicitud",
    internalKey: "request_review",
    icon: <FileSearch />,
    summary: "El IPR está siendo revisado internamente.",
    apiStepMapping: null,
  },
  {
    id: 3,
    name: "Proceso de Aprobación",
    internalKey: "approval_process",
    icon: <ShieldCheck />,
    summary: "Etapas de aprobación para la solicitud.",
    apiStepMapping: "approval",
  },
  {
    id: 4,
    name: "Validación de Proveedores",
    internalKey: "supplier_validation",
    icon: <Users />,
    summary: "Los proveedores están siendo validados para esta solicitud.",
    apiStepMapping: "supplier",
  },
  {
    id: 5,
    name: "Evaluación de Cotizaciones",
    internalKey: "evaluating_quotations",
    icon: <Briefcase />,
    summary: "Las cotizaciones están siendo evaluadas.",
    apiStepMapping: "quotations",
  },
  {
    id: 6,
    name: "OC Creada en ERP",
    internalKey: "po_created_erp",
    icon: <PackageCheck />,
    summary: "Orden de Compra generada en el ERP.",
    apiStepMapping: "po_created",
  },
];

function RenderOrder() {
  const [isLoading, setIsLoading] = useState(true);
  const [iprStatusData, setIprStatusData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [decryptedOrderID, setDecryptedOrderID] = useState(null);
  const [decryptedClientID, setDecryptedClientID] = useState(null);
  const [openStepIndex, setOpenStepIndex] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [orderLogs, setOrderLogs] = useState([]);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [openInsightSection, setOpenInsightSection] = useState(null);

  const location = useLocation();

  const toggleStepOpen = (section) => {
    setOpenStepIndex(openStepIndex === section ? null : section);

    if (
      section === "items" &&
      openStepIndex !== "items" &&
      orderItems.length === 0 &&
      decryptedClientID &&
      decryptedOrderID
    ) {
      loadOrderItems();
    }

    if (
      section === "comments" &&
      openStepIndex !== "comments" &&
      orderLogs.length === 0 &&
      decryptedClientID &&
      decryptedOrderID
    ) {
      loadOrderLogs();
    }
  };

  const toggleInsightSection = (section) => {
    setOpenInsightSection(openInsightSection === section ? null : section);
  };

  const loadOrderItems = () => {
    setIsItemsLoading(true);
    getOrderItems(decryptedClientID, decryptedOrderID)
      .then((response) => {
        if (response && response.data && Array.isArray(response.data)) {
          setOrderItems(response.data);
        } else {
          console.warn(
            "Los datos de artículos de la orden no están en el formato esperado:",
            response
          );
          setOrderItems([]);
        }
      })
      .catch((error) => {
        console.error("Error al cargar artículos de la orden:", error);
        setOrderItems([]);
      })
      .finally(() => {
        setIsItemsLoading(false);
      });
  };

  const loadOrderLogs = () => {
    setIsLogsLoading(true);
    getLogsByOrderId(decryptedClientID, decryptedOrderID)
      .then((response) => {
        if (response && response.data && Array.isArray(response.data)) {
          setOrderLogs(response.data);
        } else {
          console.warn(
            "Los datos de registros de la orden no están en el formato esperado:",
            response
          );
          setOrderLogs([]);
        }
      })
      .catch((error) => {
        console.error("Error al cargar registros de la orden:", error);
        setOrderLogs([]);
      })
      .finally(() => {
        setIsLogsLoading(false);
      });
  };

  const getApprovalStatus = (approvalPlan, approvals) => {
    if (!approvalPlan || !approvals)
      return { completed: 0, total: 0, status: "pending" };

    const totalApprovals = approvalPlan.length;
    const completedApprovals = approvals.filter(
      (approval) => approval.status === "approved"
    ).length;

    let status = "pending";
    if (completedApprovals === totalApprovals) {
      status = "completed";
    } else if (completedApprovals > 0) {
      status = "in_progress";
    }

    return { completed: completedApprovals, total: totalApprovals, status };
  };

  const getStepStatus = (stepIndex, approvalStatus, iprStatus) => {
    if (stepIndex === 0) return "completed"; // Solicitud Creada siempre completada

    if (stepIndex === 1) return "completed"; // Revisión siempre completada si llegó a aprobación

    if (stepIndex === 2) {
      // Proceso de Aprobación
      if (approvalStatus.status === "completed") return "completed";
      if (approvalStatus.status === "in_progress") return "current";
      return "pending";
    }

    // Los siguientes pasos solo se activan si la aprobación está completa
    if (approvalStatus.status !== "completed") return "pending";

    if (stepIndex === 3) return "current"; // Validación de Proveedores
    if (stepIndex === 4) return "pending"; // Evaluación de Cotizaciones
    if (stepIndex === 5) return "pending"; // OC Creada en ERP

    return "pending";
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const encodedOrderID = queryParams.get("orderID");
    const encodedClientID = queryParams.get("clientID");

    setIprStatusData(null);
    setErrorMessage("");
    setIsLoading(true);
    setDecryptedOrderID(null);
    setDecryptedClientID(null);
    setOpenStepIndex(null);
    setOrderItems([]);
    setIsItemsLoading(false);

    if (encodedOrderID && encodedClientID) {
      try {
        const orderID = encodedOrderID;
        const clientID = encodedClientID;
        setDecryptedOrderID(orderID);
        setDecryptedClientID(clientID);

        getWorkflowResultsByIdP2O(clientID, orderID)
          .then((apiResponse) => {
            if (apiResponse && apiResponse.data) {
              const apiData = apiResponse.data;

              const {
                status: apiOverallStatus,
                ai_summary,
                created_by_name,
                created_at,
                title: apiTitle,
                area,
                origin_channel,
                ia_insights,
                approval_plan,
                approvals,
                items,
                custom_fields,
                comments,
              } = apiData;

              const approvalStatus = getApprovalStatus(
                approval_plan,
                approvals
              );

              let tempProcessedSteps = displayStepsConfig.map(
                (configStep, idx) => {
                  const stepStatus = getStepStatus(
                    idx,
                    approvalStatus,
                    apiOverallStatus
                  );
                  return { ...configStep, status: stepStatus };
                }
              );

              setIprStatusData({
                title: apiTitle || "IPR sin título",
                status: apiOverallStatus,
                aiSummary: ai_summary,
                createdBy: created_by_name,
                createdAt: created_at,
                area: area,
                originChannel: origin_channel,
                iaInsights: ia_insights,
                approvalPlan: approval_plan,
                approvals: approvals,
                approvalStatus: approvalStatus,
                customFields: custom_fields,
                processedSteps: tempProcessedSteps,
                comments: comments,
              });
            } else {
              setErrorMessage(
                "No se encontraron datos para este IPR o formato de respuesta inválido."
              );
            }
          })
          .catch((error) => {
            console.error("Error de API:", error);
            setErrorMessage(
              `Error al obtener el estado del IPR: ${
                error.message || "Error desconocido"
              }`
            );
          })
          .finally(() => {
            setIsLoading(false);
          });
      } catch (error) {
        console.error("Error de desencriptación:", error);
        setErrorMessage(
          `Error al procesar los parámetros de la solicitud: ${
            error.message || "Parámetros inválidos"
          }`
        );
        setIsLoading(false);
      }
    } else {
      setErrorMessage("Faltan parámetros requeridos.");
      setIsLoading(false);
    }
  }, [location.search]);

  const getStepIcon = (step) => {
    if (step.status === "completed") {
      return <CheckCircle2 className="step-icon completed" />;
    } else if (step.status === "current") {
      return <Loader2 className="step-icon current animate-spin" />;
    } else if (step.status === "rejected") {
      return <XCircle className="step-icon rejected" />;
    } else {
      return <Circle className="step-icon pending" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStepLogs = (stepName) => {
    const stepMapping = {
      "Solicitud Creada": ["created", ""],
      "Revisión de Solicitud": ["", "created"],
      "Proceso de Aprobación": ["approval", "approved"],
      "Validación de Proveedores": ["supplier", "Suppliers"],
      "Evaluación de Cotizaciones": ["Quotations"],
      "OC Creada en ERP": ["PO Creation", "po_created"],
    };

    const searchTerms = stepMapping[stepName] || [stepName];

    return orderLogs.filter((log) =>
      searchTerms.some(
        (term) => log.step?.includes(term) || log.action?.includes(term)
      )
    );
  };

  const formatLogDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderApprovalDetails = () => {
    if (!iprStatusData.approvalPlan || !iprStatusData.approvals) return null;

    return (
      <div className="approval-details">
        <div className="approval-overview">
          <div className="overview-card">
            <div className="overview-header">
              <Shield className="overview-icon" size={24} />
              <h4>Estado General de Aprobación</h4>
            </div>
            <div className="overview-stats">
              <div className="stat-item">
                <span className="stat-number">
                  {iprStatusData.approvalStatus.completed}
                </span>
                <span className="stat-label">Aprobadas</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">
                  {iprStatusData.approvalStatus.total}
                </span>
                <span className="stat-label">Total</span>
              </div>
            </div>
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      (iprStatusData.approvalStatus.completed /
                        iprStatusData.approvalStatus.total) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
              <span className="progress-percentage">
                {Math.round(
                  (iprStatusData.approvalStatus.completed /
                    iprStatusData.approvalStatus.total) *
                    100
                )}
                %
              </span>
            </div>
          </div>
        </div>

        <div className="approval-flow">
          <h4>Flujo de Aprobación</h4>
          <div className="approval-cards-container">
            {iprStatusData.approvalPlan.map((approver, idx) => {
              const approval = iprStatusData.approvals.find(
                (a) => a.approving_user_id === approver._id
              );
              const status = approval?.status || "pending";

              return (
                <div key={idx} className="approval-flow-item">
                  <div className={`approval-card ${status}`}>
                    <div className="approval-card-header">
                      <div className="approver-details">
                        <div className="approver-avatar">
                          <span className="avatar-initials">
                            {approver.firstName?.charAt(0) || "U"}
                            {approver.lastName?.charAt(0) || "S"}
                          </span>
                        </div>
                        <div className="approver-info">
                          <span className="approver-name">
                            {approver.firstName} {approver.lastName}
                          </span>
                          <span className="approver-email">
                            {approver.email}
                          </span>
                        </div>
                      </div>

                      <div className="approval-right-section">
                        <div className="approval-status-badge">
                          {status === "approved" && (
                            <CheckCircle2
                              className="status-icon approved"
                              size={20}
                            />
                          )}
                          {status === "pending" && (
                            <Circle className="status-icon pending" size={20} />
                          )}
                          {status === "rejected" && (
                            <XCircle
                              className="status-icon rejected"
                              size={20}
                            />
                          )}
                          <span className={`status-text ${status}`}>
                            {status === "approved"
                              ? "Aprobado"
                              : status === "rejected"
                              ? "Rechazado"
                              : "Pendiente"}
                          </span>
                        </div>

                        {approval?.updated_at && (
                          <div className="approval-date">
                            <span className="date-value">
                              {new Date(approval.updated_at).toLocaleDateString(
                                "es-ES",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {approval?.comment && (
                      <div className="approval-comment">
                        <span className="comment-label">Comentario:</span>
                        <p className="comment-text">{approval.comment}</p>
                      </div>
                    )}
                  </div>

                  {/* Arrow connector to next approval */}
                  {idx < iprStatusData.approvalPlan.length - 1 && (
                    <div className="flow-connector">
                      <ArrowDown className="connector-arrow" size={20} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="render-order-fullscreen">
        <header className="render-order-header">
          <img
            src={mantisLogoLight}
            alt={`${brand} Logo`}
            className="mantis-logo"
          />
          <h1 className="mantis-title">Seguimiento de Estado IPR</h1>
        </header>
        <main className="render-order-main">
          <div className="loading-section">
            <Spinner animation="border" role="status" />
            <p>Cargando estado del IPR...</p>
          </div>
        </main>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="render-order-fullscreen">
        <header className="render-order-header">
          <img
            src={mantisLogoLight}
            alt={`${brand} Logo`}
            className="mantis-logo"
          />
          <h1 className="mantis-title">Seguimiento de Estado IPR</h1>
        </header>
        <main className="render-order-main">
          <div className="ipr-status-section">
            <div className="error-message">
              <AlertTriangle />
              <div>
                <h3>Error</h3>
                <p>{errorMessage}</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="render-order-fullscreen">
      <header className="render-order-header">
        <img
          src={mantisLogoLight}
          alt={`${brand} Logo`}
          className="mantis-logo"
        />
        <h1 className="mantis-title">Seguimiento de Estado IPR</h1>
      </header>
      <main className="render-order-main">
        <div className="ipr-status-section">
          <div className="ipr-header">
            <h2>
              <strong>IPR:</strong> {iprStatusData.title}
            </h2>
            {decryptedOrderID && (
              <div className="order-id-small">#{decryptedOrderID}</div>
            )}
            <div className="ipr-meta">
              <div className="meta-item">
                <span>Creado por: </span>
                <b>{iprStatusData.createdBy || "Desconocido"}</b>
              </div>
              <div className="meta-item">
                <span>Fecha: </span>
                <b>{formatDate(iprStatusData.createdAt)}</b>
              </div>
              {iprStatusData.area && (
                <div className="meta-item">
                  <span>Área: </span>
                  <b>{iprStatusData.area}</b>
                </div>
              )}
              {iprStatusData.originChannel && (
                <div className="meta-item">
                  <span>Canal: </span>
                  <b>{iprStatusData.originChannel}</b>
                </div>
              )}
              <div className="meta-item">
                <span>Estado: </span>
                <span
                  className={`status-badge status-${
                    iprStatusData.status?.toLowerCase() || "pending"
                  }`}
                >
                  {iprStatusData.status === "approved" && (
                    <CheckCircle2 className="status-icon" size={16} />
                  )}
                  {iprStatusData.status === "rejected" && (
                    <XCircle className="status-icon" size={16} />
                  )}
                  {iprStatusData.status === "pending" && (
                    <Loader2 className="status-icon" size={16} />
                  )}
                  <span className="status-text">
                    {iprStatusData.status === "approved"
                      ? "Aprobado"
                      : iprStatusData.status === "rejected"
                      ? "Rechazado"
                      : "Pendiente"}
                  </span>
                </span>
              </div>
              {iprStatusData.customFields &&
                Object.keys(iprStatusData.customFields).length > 0 &&
                Object.entries(iprStatusData.customFields).map(
                  ([key, value]) => (
                    <div key={key} className="meta-item">
                      <span>{key}: </span>
                      <b>{value}</b>
                    </div>
                  )
                )}
            </div>
          </div>

          {iprStatusData.status === "rejected" && (
            <div className="rejection-global-note">
              <XCircle className="rejection-icon" />
              <div className="rejection-content">
                <h3>IPR Rechazado</h3>
                <p>
                  Este IPR ha sido <strong>rechazado</strong> y no continuará su
                  procesamiento.
                </p>
                <p className="rejection-contact">
                  Por favor, contacte al equipo de compras para más información
                  o para enviar una nueva solicitud.
                </p>
              </div>
            </div>
          )}

          <div className="ipr-sections">
            {/* Elementos solicitados */}
            <div className="section-item">
              <div
                className="section-header"
                onClick={() => toggleStepOpen("items")}
              >
                <h3>Elementos Solicitados</h3>
                <span className="section-toggle-icon">
                  {openStepIndex === "items" ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </span>
              </div>
              {openStepIndex === "items" && (
                <div className="section-content">
                  {isItemsLoading ? (
                    <div className="items-loading">
                      <Spinner animation="border" size="sm" />
                      <span>Cargando artículos...</span>
                    </div>
                  ) : orderItems.length > 0 ? (
                    <div className="items-table-container">
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>Artículo</th>
                            <th>Descripción</th>
                            <th>Cantidad</th>
                            <th>Unidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderItems.map((item, itemIdx) => (
                            <tr key={itemIdx}>
                              <td>{item.name || `Artículo ${itemIdx + 1}`}</td>
                              <td>{item.description || "Sin descripción"}</td>
                              <td>{item.quantity_to_order || "N/A"}</td>
                              <td>{item.unit || "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="no-items-message">
                      No se encontraron artículos para esta orden.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Proceso de aprobacion */}
            <div className="section-item">
              <div
                className="section-header"
                onClick={() => toggleStepOpen("approvals")}
              >
                <h3>Proceso de Aprobación</h3>
                <span className="section-toggle-icon">
                  {openStepIndex === "approvals" ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </span>
              </div>
              {openStepIndex === "approvals" && (
                <div className="section-content">{renderApprovalDetails()}</div>
              )}
            </div>

            {/* Comentarios */}
            <div className="section-item">
              <div
                className="section-header"
                onClick={() => toggleStepOpen("comments")}
              >
                <h3>Comentarios</h3>
                <span className="section-toggle-icon">
                  {openStepIndex === "comments" ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </span>
              </div>
              {openStepIndex === "comments" && (
                <div className="section-content">
                  {iprStatusData.comments &&
                  iprStatusData.comments.length > 0 ? (
                    <div className="comments-list">
                      {iprStatusData.comments.map((comment, idx) => {
                        const initials = getInitials(comment.user_name);
                        const avatarColor = getAvatarColor(comment.user_name);

                        return (
                          <div key={idx} className="comment-item">
                            <div className="comment-avatar">
                              <div className={`avatar-circle ${avatarColor}`}>
                                <span className="avatar-initials">
                                  {initials}
                                </span>
                              </div>
                            </div>
                            <div className="comment-content">
                              <div className="comment-header">
                                <span className="comment-user">
                                  {comment.user_name || "Usuario"}
                                </span>
                                <span className="comment-time">
                                  {formatDate(comment.created_at)}
                                </span>
                              </div>
                              <div className="comment-text">
                                {comment.comment}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-comments-state">
                      <div className="no-comments-icon">
                        <svg
                          className="w-12 h-12 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </div>
                      <p className="no-comments-message">
                        No hay comentarios disponibles.
                      </p>
                      <p className="no-comments-subtitle">
                        Sé el primero en comentar sobre esta solicitud
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Validacion de Proveedores - Solo si está aprobado */}
            {iprStatusData.status === "approved" && (
              <div className="section-item">
                <div
                  className="section-header"
                  onClick={() => toggleStepOpen("suppliers")}
                >
                  <h3>Validación de Proveedores</h3>
                  <span className="section-toggle-icon">
                    {openStepIndex === "suppliers" ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </span>
                </div>
                {openStepIndex === "suppliers" && (
                  <div className="section-content">
                    <p className="empty-section-message">
                      Esta sección estará disponible próximamente.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Orden de compra - Solo si está aprobado */}
            {iprStatusData.status === "approved" && (
              <div className="section-item">
                <div
                  className="section-header"
                  onClick={() => toggleStepOpen("purchase_order")}
                >
                  <h3>Orden de Compra</h3>
                  <span className="section-toggle-icon">
                    {openStepIndex === "purchase_order" ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </span>
                </div>
                {openStepIndex === "purchase_order" && (
                  <div className="section-content">
                    <p className="empty-section-message">
                      Esta sección estará disponible próximamente.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default RenderOrder;
